if (!customElements.get('cart-recommendations')) {
  customElements.define(
    'cart-recommendations',
    class CartRecommendations extends HTMLElement {
      connectedCallback() {
        this.cart = this.closest('cart-drawer');
        this.inner = this.closest('.drawer__inner');
        this.popup = this.inner && this.inner.querySelector('.cart-rec-popup');
        this.errorEl = this.querySelector('.cart-rec__error');
        this.activeButton = null;

        this.addEventListener('click', (event) => {
          const button = event.target.closest('.cart-rec__add');
          if (button) this.onAddClick(button);
        });

        this.initSlider();
        const items = this.closest('cart-drawer-items');
        if (items) items.addEventListener('scroll', () => this.closePopup(), { passive: true });

        this.bindPopup();
      }

      initSlider() {
        const container = this.querySelector('.cart-rec__slider');
        if (!container || typeof Swiper === 'undefined') return;

        const mobile = parseFloat(this.dataset.slidesMobile) || 2.5;
        const desktop = parseFloat(this.dataset.slidesDesktop) || 3.5;
        this.swiper = new Swiper(container, {
          slidesPerView: mobile,
          spaceBetween: 10,
          watchOverflow: true,
          scrollbar: { el: this.querySelector('.cart-rec__scrollbar'), draggable: true, hide: false },
          breakpoints: { 750: { slidesPerView: desktop, spaceBetween: 12 } },
          on: {
            sliderMove: () => this.closePopup(),
            slideChange: () => this.closePopup(),
          },
        });
      }

      disconnectedCallback() {
        if (this.swiper) this.swiper.destroy(true, true);
        this.swiper = null;
      }

      // The popup lives outside <cart-drawer-items>, so it survives cart re-renders
      // while this element is replaced. Bind once and always delegate to the
      // current instance (popup._recOwner).
      bindPopup() {
        const popup = this.popup;
        if (!popup) return;
        popup._recOwner = this;
        if (popup.dataset.bound) return;
        popup.dataset.bound = 'true';

        popup
          .querySelector('.cart-rec-popup__close')
          .addEventListener('click', () => popup._recOwner.closePopup(true));
        popup.addEventListener('keyup', (event) => {
          if (event.code !== 'Escape') return;
          event.stopPropagation(); // keep the drawer itself open
          popup._recOwner.closePopup(true);
        });
        popup.addEventListener('change', (event) => {
          if (event.target.matches('input[type="radio"]')) popup._recOwner.updateVariant();
        });
        popup.addEventListener('submit', (event) => {
          event.preventDefault();
          popup._recOwner.onPopupSubmit();
        });
        // Click anywhere else in the drawer closes the popup
        this.inner.addEventListener('click', (event) => {
          if (popup.hidden || popup.contains(event.target) || event.target.closest('.cart-rec__add')) return;
          popup._recOwner.closePopup();
        });
      }

      onAddClick(button) {
        if (button.getAttribute('aria-disabled') === 'true') return;
        this.showError(this.errorEl, '');

        if (button.dataset.hasChoices === 'true') {
          if (this.activeButton === button && !this.popup.hidden) return this.closePopup(true);
          return this.openPopup(button);
        }

        this.closePopup();
        const formData = new FormData();
        formData.append('id', button.dataset.variantId);
        formData.append('quantity', 1);
        button.setAttribute('aria-disabled', 'true');
        this.addToCart(formData)
          .catch((message) => this.showError(this.errorEl, message))
          .finally(() => button.removeAttribute('aria-disabled'));
      }

      openPopup(button) {
        const template = button.closest('.cart-rec__item').querySelector('template[data-popup-template]');
        if (!template || !this.popup) return;

        const content = this.popup.querySelector('.cart-rec-popup__content');
        content.innerHTML = '';
        content.appendChild(template.content.cloneNode(true));
        this.variants = JSON.parse(content.querySelector('[data-popup-variants]').textContent);

        this.closePopup(false, true);
        this.activeButton = button;
        button.setAttribute('aria-expanded', 'true');
        this.updateVariant();

        this.popup.style.visibility = 'hidden';
        this.popup.hidden = false;
        this.positionPopup(button.closest('.cart-rec__card'));
        this.popup.style.visibility = '';
        this.setOverlay(true);
        this.popup.focus({ preventScroll: true });
      }

      closePopup(returnFocus = false, keepContent = false) {
        if (!this.popup) return;
        if (this.activeButton) {
          this.activeButton.setAttribute('aria-expanded', 'false');
          if (returnFocus) this.activeButton.focus({ preventScroll: true });
        }
        if (keepContent) return;
        this.popup.hidden = true;
        this.setOverlay(false);
        this.activeButton = null;
      }

      setOverlay(show) {
        const overlay = this.inner.querySelector('.cart-rec-popup-overlay');
        if (overlay) overlay.hidden = !show;
      }

      // Place the popup to the right of the card when it fits, otherwise
      // right-aligned beside it (below, or above), clamped inside the drawer.
      positionPopup(card) {
        const inner = this.inner;
        const popup = this.popup;
        const gap = 8;
        const ir = inner.getBoundingClientRect();
        const cr = card.getBoundingClientRect();
        const pw = popup.offsetWidth;
        const ph = popup.offsetHeight;
        const iw = inner.clientWidth;
        const ih = inner.clientHeight;
        const cardLeft = cr.left - ir.left - inner.clientLeft;
        const cardRight = cardLeft + cr.width;
        const cardTop = cr.top - ir.top - inner.clientTop;
        const cardBottom = cardTop + cr.height;
        const clamp = (value, max) => Math.max(gap, Math.min(value, max));

        let left;
        let top;
        if (cardRight + gap + pw <= iw - gap) {
          left = cardRight + gap;
          top = clamp(cardTop, ih - ph - gap);
        } else {
          left = iw - pw - gap * 1.5;
          top = cardBottom + gap;
          if (top + ph > ih - gap) top = cardTop - ph - gap;
          top = clamp(top, ih - ph - gap);
        }
        popup.style.left = `${Math.max(gap, left)}px`;
        popup.style.top = `${top}px`;
      }

      get selectedVariant() {
        const fieldsets = this.popup.querySelectorAll('.cart-rec-popup__option');
        const chosen = [...fieldsets].map((fs) => {
          const input = fs.querySelector('input:checked');
          return input ? input.value : null;
        });
        return this.variants.find((variant) => variant.options.every((option, i) => option === chosen[i]));
      }

      updateVariant() {
        const variant = this.selectedVariant;
        const popup = this.popup;
        const price = popup.querySelector('[data-popup-price]');
        const compare = popup.querySelector('[data-popup-compare]');
        const submit = popup.querySelector('.cart-rec-popup__submit');
        const label = submit.querySelector('span');
        const image = popup.querySelector('.cart-rec-popup__image');
        const idInput = popup.querySelector('input[name="id"]');

        this.showError(popup.querySelector('.cart-rec-popup__error'), '');
        if (!variant) {
          idInput.value = '';
          submit.setAttribute('aria-disabled', 'true');
          label.textContent = window.variantStrings?.unavailable || 'Unavailable';
          return;
        }

        idInput.value = variant.id;
        price.textContent = variant.price;
        compare.textContent = variant.compare || '';
        compare.hidden = !variant.compare;
        if (image && variant.image) image.src = variant.image;

        if (variant.available) {
          submit.removeAttribute('aria-disabled');
          label.textContent = window.variantStrings?.addToCart || 'Add to cart';
        } else {
          submit.setAttribute('aria-disabled', 'true');
          label.textContent = window.variantStrings?.soldOut || 'Sold out';
        }
      }

      onPopupSubmit() {
        const submit = this.popup.querySelector('.cart-rec-popup__submit');
        const variant = this.selectedVariant;
        if (!variant || !variant.available || submit.getAttribute('aria-disabled') === 'true') return;

        const form = this.popup.querySelector('[data-popup-form]');
        const errorEl = this.popup.querySelector('.cart-rec-popup__error');
        submit.setAttribute('aria-disabled', 'true');
        this.addToCart(new FormData(form))
          .catch((message) => {
            this.showError(errorEl, message);
            submit.removeAttribute('aria-disabled');
          });
        // On success the drawer re-renders, which removes this popup.
      }

      addToCart(formData) {
        const cart = this.cart;
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
        config.body = formData;

        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'cart-recommendations',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              return Promise.reject(response.description || response.message || 'Error');
            }
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-recommendations',
              productVariantId: formData.get('id'),
              cartData: response,
            });
            cart.renderContents(response);
          })
          .catch((error) => {
            if (typeof error === 'string') return Promise.reject(error);
            console.error(error);
            return Promise.reject(window.cartStrings?.error || 'Something went wrong');
          });
      }

      showError(element, message) {
        if (!element) return;
        element.textContent = message || '';
        element.hidden = !message;
      }
    }
  );
}
